/**
 * Maps a Vue route (plus the active client-card tab) to a smart-prompts context.
 *
 * Each context drives which prompts the panel surfaces. Keep the
 * shape narrow: { kind, variables } where `kind` keys into i18n
 * `pages.smartPrompts.contexts[kind]` and `variables` are interpolated
 * into prompt bodies via `{{name}}` placeholders.
 *
 * The client card (`view-athlete`) is a single route whose tabs switch via
 * history.pushState, so the active tab can't be read from the route. The
 * caller passes it in via `opts.athleteTab` (the tab hash, e.g. 'measurements').
 */
export function contextForRoute(route, opts = {}) {
  if (!route || !route.name) return { kind: 'generic', variables: {} };

  const name = route.name;
  const params = route.params || {};
  const athleteTab = opts.athleteTab;

  // Dashboard and calendar overlap heavily (today's schedule, revenue, day
  // wrap-up), so they share one "schedule & clients" prompt set.
  if (name === 'home' || name === 'dashboard' || name === 'calendar') {
    return { kind: 'practice', variables: {} };
  }

  if (name === 'view-athlete') {
    const variables = { athleteId: params.athleteId };
    if (athleteTab === 'measurements') return { kind: 'athleteMeasurements', variables };
    if (athleteTab === 'mobile-app') return { kind: 'athleteMealLog', variables };
    return { kind: 'athlete', variables };
  }

  if (name === 'athlete-diet-plan') {
    return {
      kind: 'dietPlan',
      variables: { athleteId: params.athleteId, dietPlanId: params.dietPlanId },
    };
  }

  if (name === 'athletes' || name === 'athlete-list') {
    return { kind: 'athleteList', variables: {} };
  }

  return { kind: 'generic', variables: {} };
}

export function interpolate(template, variables) {
  return String(template ?? '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => {
    const value = variables?.[key];
    return value == null ? '' : String(value);
  });
}
